import * as Sentry from '@sentry/browser'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  DomesticCartBusItem,
  DomesticCartBusOption,
} from '~/api/converters/domestic/cart/ProductSection/Bus/index.type'
import {
  DomesticCartLeisureItem,
  DomesticCartLeisureOption,
} from '~/api/converters/domestic/cart/ProductSection/Leisure/index.type'
import {
  DomesticCartPropertyItem,
  DomesticCartPropertyOption,
} from '~/api/converters/domestic/cart/ProductSection/Property/index.type'
import {
  DomesticCartTrainItem,
  DomesticCartTrainOption,
} from '~/api/converters/domestic/cart/ProductSection/Train/index.type'
import { getCouponPlatformMaxDiscount } from '~/api/converters/domestic/order/Coupons/MaxDiscountCoupons/getCouponPlatformMaxDiscount'
import { getDomesticCouponMaxDiscount } from '~/api/converters/domestic/order/Coupons/MaxDiscountCoupons/getDomesticCouponMaxDiscount'
import { MaxCouponDiscountPropertyRequiredParams } from '~/api/converters/domestic/order/Discount'
import { MaxCouponDiscountCouponPlatformRequiredParams } from '~/api/converters/domestic/order/Discount/CouponSelectModal/couponPlatform'
import { MaxCouponDiscount } from '~/api/converters/domestic/order/Discount/index.type'
import { ItemType, TMaxDiscountLimit } from '~/api/routes/domestic/order/controller/type'
import { ICouponInstances } from '~/api/services/coupon-platform/index.type'
import { TDomesticCoupon } from '~/api/services/coupon/index.type'
import {
  getTrainPackageDiscountAmount,
  TGetTrainPackageDiscountAmountParams,
} from '~/api/utils/train'
import useDomesticCartPrice from '~/app/components-nds/domestic/cart/hooks/use-domestic-cart-price'
import { isTruthy } from '~/app/utils'

interface ICouponMaxDiscountCache {
  [ItemType.Property]: Record<string, MaxCouponDiscount>
  [ItemType.Leisure]: Record<string, MaxCouponDiscount>
  [ItemType.Train]: Record<string, MaxCouponDiscount>
  [ItemType.Bus]: Record<string, MaxCouponDiscount>
}
interface ICacheId {
  cacheId: string
}

/**
 * 장바구니 > 쿠폰 할인 Hook
 */
const useDomesticCartDiscount = ({
  maxDiscountLimit,
  isMember,
}: {
  maxDiscountLimit: TMaxDiscountLimit
  isMember: boolean
}) => {
  const { selectedCartOptions } = useDomesticCartPrice()

  const couponMaxDiscountCache = useRef<ICouponMaxDiscountCache>({
    property: {},
    leisure: {},
    train: {},
    bus: {},
  })

  // 선택된 아이템들의 캐시 키를 반환
  const getKeyOfSelectedItems = useCallback((selectedItems: ICacheId[]) => {
    return selectedItems
      .map(item => {
        return item.cacheId
      })
      .join(':')
  }, [])

  // 최대할인금액 계산 함수 인자 인터페이스에 맞춰 cartItems 를 변환
  const { properties, leisures, trains, buses } = useMemo(() => {
    let [pcnt, lcnt, tcnt, bcnt] = [0, 0, 0, 0]

    return selectedCartOptions.reduce<{
      properties: (MaxCouponDiscountPropertyRequiredParams & ICacheId)[]
      leisures: (MaxCouponDiscountCouponPlatformRequiredParams & ICacheId)[]
      trains: (MaxCouponDiscountCouponPlatformRequiredParams & ICacheId)[]
      buses: (MaxCouponDiscountCouponPlatformRequiredParams & ICacheId)[]
    }>(
      (acc, cur) => {
        switch (cur.type) {
          case 'DOMESTIC_RESERVATION':
            acc.properties = acc.properties.concat(
              (cur as DomesticCartPropertyItem).options.map(
                (option: DomesticCartPropertyOption) => ({
                  cacheId: option.optionId,
                  price: option.price,
                  type: ItemType.Property,
                  typeIdx: pcnt++,
                  useType: (option.useType === 'DAY_USE'
                    ? 'R'
                    : option.useType === 'OVER_NIGHT'
                      ? 'S'
                      : 'D') as 'D' | 'R' | 'S',
                  id: cur.propertyId,
                  checkInDate: option.checkInDate,
                  coupons: (option.coupons || []) as TDomesticCoupon[],
                  additionalOneCoupons: (option.additionalOneCoupons || []) as TDomesticCoupon[],
                  additionalTwoCoupons: (option.additionalTwoCoupons || []) as TDomesticCoupon[],
                })
              )
            )
            break
          case 'DOMESTIC_LEISURE':
            acc.leisures = acc.leisures.concat(
              (cur as DomesticCartLeisureItem).options.map((option: DomesticCartLeisureOption) => ({
                cacheId: option.optionId,
                id: +option.optionId,
                price: option.price,
                type: ItemType.Leisure,
                typeIdx: lcnt++,
                coupons: (option.coupons || []) as ICouponInstances[],
              }))
            )
            break
          case 'DOMESTIC_TRAIN':
            acc.trains = acc.trains.concat(
              (cur as DomesticCartTrainItem).options.map((option: DomesticCartTrainOption) => ({
                cacheId: option.optionId,
                price: option.price,
                type: ItemType.Train,
                typeIdx: tcnt++,
                coupons: (option.coupons || []) as ICouponInstances[],
              }))
            )
            break
          case 'DOMESTIC_BUS':
            acc.buses = acc.buses.concat(
              (cur as DomesticCartBusItem).options.map((option: DomesticCartBusOption) => ({
                cacheId: option.optionId,
                price: option.price,
                type: ItemType.Bus,
                typeIdx: bcnt++,
                coupons: (option.coupons || []) as ICouponInstances[],
              }))
            )
            break
        }
        return acc
      },
      {
        properties: [],
        leisures: [],
        trains: [],
        buses: [],
      }
    )
  }, [selectedCartOptions])

  const isMaxLengthExceeded = useMemo(() => {
    return (
      properties.length + leisures.length + trains.length + buses.length >
      maxDiscountLimit.maxLength
    )
  }, [properties, leisures, trains, buses, maxDiscountLimit])

  // 묶음할인 계산 함수 인자 인터페이스에 맞춰 cartItems 를 변환
  const convertedCartItemsForTrainPackageDiscount =
    useMemo<TGetTrainPackageDiscountAmountParams>(() => {
      return selectedCartOptions
        .map(item => {
          if (item.type === 'DOMESTIC_TRAIN') {
            return (item as DomesticCartTrainItem).options.map(option => {
              return {
                price: option.price,
                packageDiscount: option.packageDiscount || 0,
              }
            })
          }
          if (item.type === 'DOMESTIC_RESERVATION') {
            return item.options.map(property => {
              return {
                price: property.price,
                packageDiscount: 0,
              }
            })
          }
          return
        })
        .flat()
        .filter(isTruthy)
    }, [selectedCartOptions])

  // 선택된 cartItems 의 캐시키와 최대할인금액 정보를 반환
  const getSelectedKeyAndMaxDiscount = useCallback(
    ({
      items,
      getMaxDiscount,
      itemType,
    }: {
      itemType: ItemType
      items: ICacheId[]
      getMaxDiscount: (params, maxDiscountLimit) => MaxCouponDiscount
    }) => {
      const selectedKey = getKeyOfSelectedItems(items)
      if (isMaxLengthExceeded) {
        return {
          selectedKey,
          maxDiscount: {
            maxDiscount: 0,
            discountAppliedItems: [],
          },
        }
      }

      const maxDiscount =
        couponMaxDiscountCache.current[itemType][selectedKey] ||
        getMaxDiscount(items, maxDiscountLimit)
      return { selectedKey, maxDiscount }
    },
    [isMaxLengthExceeded, maxDiscountLimit]
  )

  // 최대할인 금액
  const {
    propertyCouponMaxDiscount,
    leisureCouponMaxDiscount,
    trainCouponMaxDiscount,
    busCouponMaxDiscount,
  } = useMemo(() => {
    const propertyCouponMaxDiscount = getSelectedKeyAndMaxDiscount({
      items: properties,
      getMaxDiscount: getDomesticCouponMaxDiscount,
      itemType: ItemType.Property,
    })
    const leisureCouponMaxDiscount = getSelectedKeyAndMaxDiscount({
      items: leisures,
      itemType: ItemType.Leisure,
      getMaxDiscount: getCouponPlatformMaxDiscount,
    })
    const trainCouponMaxDiscount = getSelectedKeyAndMaxDiscount({
      items: trains,
      itemType: ItemType.Train,
      getMaxDiscount: getCouponPlatformMaxDiscount,
    })
    const busCouponMaxDiscount = getSelectedKeyAndMaxDiscount({
      items: buses,
      itemType: ItemType.Bus,
      getMaxDiscount: getCouponPlatformMaxDiscount,
    })

    return {
      propertyCouponMaxDiscount,
      leisureCouponMaxDiscount,
      trainCouponMaxDiscount,
      busCouponMaxDiscount,
    }
  }, [properties, leisures, trains, buses])

  // 기차 묶음할인 가격
  const trainPackageDiscountAmount = useMemo(
    () => getTrainPackageDiscountAmount(convertedCartItemsForTrainPackageDiscount),
    [selectedCartOptions]
  )

  // 선택된 cartItems 의 최대할인 금액과 타입을 반환
  const { maxDiscountAmount, maxDiscountTypeName, isTimeout } = useMemo(() => {
    if (
      propertyCouponMaxDiscount.maxDiscount.isTimeout ||
      leisureCouponMaxDiscount.maxDiscount.isTimeout ||
      trainCouponMaxDiscount.maxDiscount.isTimeout ||
      busCouponMaxDiscount.maxDiscount.isTimeout
    ) {
      return {
        maxDiscountAmount: 0,
        maxDiscountTypeName: '',
        isTimeout: true,
      }
    }

    const couponMaxDiscountAmount =
      propertyCouponMaxDiscount.maxDiscount.maxDiscount +
      trainCouponMaxDiscount.maxDiscount.maxDiscount +
      busCouponMaxDiscount.maxDiscount.maxDiscount +
      leisureCouponMaxDiscount.maxDiscount.maxDiscount

    const maxDiscountAmount = Math.max(couponMaxDiscountAmount, trainPackageDiscountAmount)

    return {
      maxDiscountAmount,
      maxDiscountTypeName:
        couponMaxDiscountAmount > trainPackageDiscountAmount ? '쿠폰 할인' : '묶음 할인',
      isTimeout: false,
    }
  }, [selectedCartOptions])

  // 체크 박스 토글 등 cartItems 가 변경될 때마다 부하가 큰 최대할인함수 실행을 최소화하기 위해 최대할인금액 정보 캐시 업데이트
  useEffect(() => {
    const { isTimeout: isPropertyTimeout } = propertyCouponMaxDiscount.maxDiscount
    const { isTimeout: isLeisureTimeout } = leisureCouponMaxDiscount.maxDiscount
    const { isTimeout: isTrainTimeout } = trainCouponMaxDiscount.maxDiscount
    const { isTimeout: isBusTimeout } = busCouponMaxDiscount.maxDiscount

    if (isPropertyTimeout || isLeisureTimeout || isTrainTimeout || isBusTimeout) {
      Sentry.withScope(scope => {
        const setMashupDiscountInfo = (
          label: string,
          mashup: { coupons: any[]; additionalOneCoupons?: any[]; additionalTwoCoupons?: any[] }[]
        ) => {
          scope.setExtra(
            label,
            mashup.map(p => ({
              couponLength: p.coupons.length,
              additionalOneCouponLength: p.additionalOneCoupons?.length,
              additionalTwoCouponLength: p.additionalTwoCoupons?.length,
            }))
          )
        }
        setMashupDiscountInfo('propertyMashup 할인 내용', properties)
        setMashupDiscountInfo('leisureMashup 할인 내용', leisures)
        setMashupDiscountInfo('trainMashup 할인 내용', trains)
        setMashupDiscountInfo('busMashup 할인 내용', buses)
        Sentry.captureMessage('장바구니 > 쿠폰 최대 할인 시간 초과')
      })
    }

    couponMaxDiscountCache.current = {
      [ItemType.Property]: {
        ...couponMaxDiscountCache.current[ItemType.Property],
        [propertyCouponMaxDiscount.selectedKey]: propertyCouponMaxDiscount.maxDiscount,
      },
      [ItemType.Leisure]: {
        ...couponMaxDiscountCache.current[ItemType.Leisure],
        [leisureCouponMaxDiscount.selectedKey]: leisureCouponMaxDiscount.maxDiscount,
      },
      [ItemType.Train]: {
        ...couponMaxDiscountCache.current[ItemType.Train],
        [trainCouponMaxDiscount.selectedKey]: trainCouponMaxDiscount.maxDiscount,
      },
      [ItemType.Bus]: {
        ...couponMaxDiscountCache.current[ItemType.Bus],
        [busCouponMaxDiscount.selectedKey]: busCouponMaxDiscount.maxDiscount,
      },
    }
  }, [selectedCartOptions])

  if (!isMember) {
    return {
      maxDiscountAmount: 0,
      maxDiscountTypeName: '',
      isTimeout: false,
      isMaxLengthExceeded: false,
    }
  }

  return {
    // 최대 할인 금액
    maxDiscountAmount,
    // 할인 타입 (쿠폰 or 묶음)
    maxDiscountTypeName,
    isTimeout,
    isMaxLengthExceeded,
  }
}

export default useDomesticCartDiscount
